/**
 * Status visuals and predefined filters for the suivi des arrêts de travail.
 *
 * One palette for every status the module shows — the arrêt's own (Initial / Prolongation)
 * and each organisme's reimbursement status — so a colour means the same thing in the grid,
 * the mobile cards, the details dialog and the form's dropdowns. Explicit colours rather
 * than MUI's `color` prop: the theme has five intents and this needs seven distinct ones.
 */
import React, { useMemo } from "react";
import { Box, Chip, Stack } from "@mui/material";
import moment from "moment";
import { Localize } from "../../app/lang/langs";

/** `fg` is the text and dot colour, `bg` the chip fill. */
export const STATUS_PALETTE = {
  PENDING: { fg: "#B45309", bg: "#FFF4E5" },
  PARTIAL: { fg: "#0369A1", bg: "#E0F2FE" },
  REIMBURSED: { fg: "#15803D", bg: "#E7F6EC" },
  REFUSED: { fg: "#B91C1C", bg: "#FDECEC" },
  NOT_APPLICABLE: { fg: "#5F6B7A", bg: "#EEF0F3" },
  INITIAL: { fg: "#3949AB", bg: "#E8EAF6" },
  PROLONGATION: { fg: "#8E24AA", bg: "#F3E5F5" },
};

const NEUTRAL = { fg: "#5F6B7A", bg: "#EEF0F3" };

/** The translation key naming a status, whichever family it belongs to. */
export const statusLabelKey = (status) =>
  status === "INITIAL" || status === "PROLONGATION"
    ? `WORK_STOPPAGE_${status}`
    : `WORK_STOPPAGE_REIMBURSEMENT_${status}`;

/** A small filled circle in the status colour — used beside dropdown options. */
export function StatusDot({ status, color }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        flexShrink: 0,
        bgcolor: color || (STATUS_PALETTE[status] || NEUTRAL).fg,
      }}
    />
  );
}

/** A dot + label, for the options of a status dropdown. */
export function StatusOption({ status }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" component="span">
      <StatusDot status={status} />
      <span>{Localize(statusLabelKey(status))}</span>
    </Stack>
  );
}

/**
 * A status as a coloured chip. `prefix` names the organisme when two statuses sit side by
 * side without a column header (the mobile cards). Renders nothing for an empty status.
 */
export function StatusChip({ status, prefix, size = "small", sx }) {
  if (!status) return null;
  const palette = STATUS_PALETTE[status] || NEUTRAL;
  const label = Localize(statusLabelKey(status));
  return (
    <Chip
      size={size}
      label={prefix ? `${prefix} : ${label}` : label}
      sx={{
        fontWeight: 600,
        borderRadius: "8px",
        color: palette.fg,
        bgcolor: palette.bg,
        border: `1px solid ${palette.fg}33`,
        ...sx,
      }}
    />
  );
}

const isSettledPrevoyance = (status) => status === "REIMBURSED" || status === "NOT_APPLICABLE";

/**
 * The predefined filters — the questions Paie actually opens this screen with. Each one is
 * a plain predicate over a row, applied before the grid's own column filters, so a preset
 * and a column filter combine instead of replacing each other.
 */
export const WORK_STOPPAGE_PRESETS = [
  { value: "ALL", color: NEUTRAL.fg, test: () => true },
  { value: "CPAM_PENDING", color: STATUS_PALETTE.PENDING.fg, test: (row) => row.cpam_reimbursement_status === "PENDING" },
  { value: "PREVOYANCE_PENDING", color: STATUS_PALETTE.PENDING.fg, test: (row) => row.prevoyance_reimbursement_status === "PENDING" },
  {
    value: "PARTIAL", color: STATUS_PALETTE.PARTIAL.fg,
    test: (row) => row.cpam_reimbursement_status === "PARTIAL" || row.prevoyance_reimbursement_status === "PARTIAL",
  },
  {
    value: "REFUSED", color: STATUS_PALETTE.REFUSED.fg,
    test: (row) => row.cpam_reimbursement_status === "REFUSED" || row.prevoyance_reimbursement_status === "REFUSED",
  },
  {
    value: "SETTLED", color: STATUS_PALETTE.REIMBURSED.fg,
    test: (row) => row.cpam_reimbursement_status === "REIMBURSED" && isSettledPrevoyance(row.prevoyance_reimbursement_status),
  },
  {
    value: "ONGOING", color: STATUS_PALETTE.INITIAL.fg,
    test: (row) => !row.end_date || !moment(row.end_date).isBefore(moment(), "day"),
  },
  { value: "PROLONGATIONS", color: STATUS_PALETTE.PROLONGATION.fg, test: (row) => row.stoppage_status === "PROLONGATION" },
];

/** The rows a preset keeps. An unknown preset keeps everything. */
export function applyWorkStoppagePreset(rows, preset) {
  const definition = WORK_STOPPAGE_PRESETS.find((p) => p.value === preset);
  return definition ? (rows || []).filter(definition.test) : rows || [];
}

/**
 * The preset filters as a row of chips, each with the number of arrêts it would show.
 * The counts are taken over the full list, so they read the same whichever preset is on.
 */
export function WorkStoppagePresetFilters({ rows, value, onChange, sx }) {
  const counts = useMemo(() => {
    const result = {};
    for (const preset of WORK_STOPPAGE_PRESETS)
      result[preset.value] = (rows || []).filter(preset.test).length;
    return result;
  }, [rows]);

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, ...sx }}>
      {WORK_STOPPAGE_PRESETS.map((preset) => {
        const selected = value === preset.value;
        return (
          <Chip
            key={preset.value}
            size="small"
            clickable
            onClick={() => onChange(preset.value)}
            icon={<StatusDot color={selected ? "#fff" : preset.color} />}
            label={`${Localize(`WORK_STOPPAGE_PRESET_${preset.value}`)} (${counts[preset.value]})`}
            aria-pressed={selected}
            sx={{
              fontWeight: 600,
              borderRadius: "8px",
              pl: 0.5,
              "& .MuiChip-icon": { ml: 0.5 },
              color: selected ? "#fff" : "text.primary",
              bgcolor: selected ? preset.color : "transparent",
              border: `1px solid ${selected ? preset.color : "rgba(0,0,0,0.12)"}`,
              "&:hover": { bgcolor: selected ? preset.color : "rgba(0,0,0,0.04)" },
            }}
          />
        );
      })}
    </Box>
  );
}
